import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiGet, apiPatch } from '../../lib/api.js';
import { Card, Field, SaveButton, LoadingPanel, isDirty } from './_shared.jsx';
import { Phone } from 'lucide-react';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
function hourLabel(h) {
  const ampm = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
}

export default function NotificationsTab() {
  const [loaded, setLoaded] = useState(null); // { notifications, phone, timezone }
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet('/agents/me/notifications')
      .then((r) => {
        if (r?.ok) {
          const snap = {
            notifications: {
              sms: r.notifications?.sms !== false,
              smsOnLeadAssign: r.notifications?.smsOnLeadAssign !== false,
              quietHours: {
                enabled: !!r.notifications?.quietHours?.enabled,
                startHour: r.notifications?.quietHours?.startHour ?? 21,
                endHour: r.notifications?.quietHours?.endHour ?? 7,
                timezone: r.notifications?.quietHours?.timezone || '',
              },
              duplicateAlert: {
                enabled: r.notifications?.duplicateAlert?.enabled !== false, // default true
                windowDays: r.notifications?.duplicateAlert?.windowDays ?? 30,
              },
            },
            phone: r.phone || '',
            timezone: r.timezone || '',
          };
          setLoaded(snap);
          setDraft(snap);
        }
      })
      .catch(() => toast.error('Failed to load notification settings'));
  }, []);

  async function save() {
    if (!draft) return;
    const prev = loaded;
    setBusy(true);
    setLoaded(draft); // optimistic
    try {
      const r = await apiPatch('/agents/me/notifications', {
        sms: draft.notifications.sms,
        smsOnLeadAssign: draft.notifications.smsOnLeadAssign,
        quietHours: {
          enabled: draft.notifications.quietHours.enabled,
          startHour: draft.notifications.quietHours.startHour,
          endHour: draft.notifications.quietHours.endHour,
        },
        duplicateAlert: {
          enabled: draft.notifications.duplicateAlert.enabled,
          windowDays: draft.notifications.duplicateAlert.windowDays,
        },
      });
      if (!r?.ok) throw new Error('Save failed');
      const snap = {
        notifications: r.notifications,
        phone: r.phone || draft.phone,
        timezone: r.timezone || draft.timezone,
      };
      setLoaded(snap);
      setDraft(snap);
      toast.success('Notification settings saved');
    } catch (e) {
      setLoaded(prev);
      setDraft(prev);
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  if (!draft) return <LoadingPanel label="Loading notification settings…" />;
  const dirty = isDirty(draft, loaded);
  const n = draft.notifications;
  const tzLabel = n.quietHours.timezone || draft.timezone || 'tenant default';

  const setN = (patch) => setDraft({ ...draft, notifications: { ...n, ...patch } });
  const setQH = (patch) =>
    setDraft({ ...draft, notifications: { ...n, quietHours: { ...n.quietHours, ...patch } } });
  const setDA = (patch) =>
    setDraft({ ...draft, notifications: { ...n, duplicateAlert: { ...n.duplicateAlert, ...patch } } });

  const DUPE_WINDOWS = [30, 60, 90, 120];

  return (
    <div className="space-y-4">
      <Card
        title="Lead Notifications"
        description="Get a text on your cell the moment a new lead is assigned to you."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        {/* SMS-on-assignment toggle */}
        <label className="flex items-start justify-between gap-4 py-2">
          <div>
            <div className="text-sm font-medium">SMS me when a lead is assigned</div>
            <div className="text-xs text-white/50">
              We&apos;ll text your cell phone each time a lead lands in your pipeline.
            </div>
          </div>
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-violet-500 shrink-0"
            data-testid="notif-sms-on-assign"
            checked={n.smsOnLeadAssign && n.sms}
            onChange={(e) => setN({ smsOnLeadAssign: e.target.checked, sms: e.target.checked ? true : n.sms })}
          />
        </label>

        {/* Cell phone display */}
        <Field label="Your cell phone" hint="This is where assignment texts are sent.">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-white/40" />
            <span className="text-sm" data-testid="notif-cell-phone">
              {draft.phone || <span className="text-amber-300">No cell phone on file</span>}
            </span>
            <a href="/settings?tab=profile" className="text-xs text-violet-300 hover:text-violet-200 ml-2">
              Update
            </a>
          </div>
        </Field>
      </Card>

      {/* Quiet Hours */}
      <Card
        title="Quiet Hours"
        description="Pause assignment texts during set hours. Leads still arrive — you just won't be texted."
      >
        <label className="flex items-center justify-between gap-4 py-2">
          <div className="text-sm font-medium">Enable quiet hours</div>
          <input
            type="checkbox"
            className="h-5 w-5 accent-violet-500 shrink-0"
            data-testid="notif-quiet-enabled"
            checked={n.quietHours.enabled}
            onChange={(e) => setQH({ enabled: e.target.checked })}
          />
        </label>

        {n.quietHours.enabled && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Start (no texts after)">
              <select
                className="input"
                data-testid="notif-quiet-start"
                value={n.quietHours.startHour}
                onChange={(e) => setQH({ startHour: Number(e.target.value) })}
              >
                {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </Field>
            <Field label="End (texts resume at)">
              <select
                className="input"
                data-testid="notif-quiet-end"
                value={n.quietHours.endHour}
                onChange={(e) => setQH({ endHour: Number(e.target.value) })}
              >
                {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </Field>
            <Field label="Timezone" hint="Quiet hours use this timezone.">
              <input className="input opacity-60" value={tzLabel} disabled data-testid="notif-quiet-tz" />
            </Field>
          </div>
        )}
      </Card>

      {/* Duplicate-lead alerts */}
      <Card
        title="Duplicate Lead Alerts"
        description="Know when a new lead is one you already have from the same vendor, so you can request a replacement."
      >
        <label className="flex items-start justify-between gap-4 py-2">
          <div>
            <div className="text-sm font-medium">Alert me about duplicate leads</div>
            <div className="text-xs text-white/50">
              Get an in-app and text alert when you receive a new lead with the same phone number
              from the same vendor you already have — so you can request a replacement. It never
              blocks the lead.
            </div>
          </div>
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-violet-500 shrink-0"
            data-testid="notif-dupe-enabled"
            checked={n.duplicateAlert.enabled}
            onChange={(e) => setDA({ enabled: e.target.checked })}
          />
        </label>

        {n.duplicateAlert.enabled && (
          <Field label="Look back over the past">
            <select
              className="input"
              data-testid="notif-dupe-window"
              value={n.duplicateAlert.windowDays}
              onChange={(e) => setDA({ windowDays: Number(e.target.value) })}
            >
              {DUPE_WINDOWS.map((d) => <option key={d} value={d}>{`${d} days`}</option>)}
            </select>
          </Field>
        )}
      </Card>
    </div>
  );
}
